import { useState } from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { TASK_STATUSES } from "@/lib/constants";
import { fmtDate } from "@/lib/api";
import { CalendarClock, User } from "lucide-react";

const COL_ACCENT = {
  "À faire": "border-t-slate-400",
  "En cours": "border-t-brand-500",
  "Bloquée": "border-t-rose-500",
  "Terminée": "border-t-emerald-500",
};

export default function KanbanBoard({ tasks, onMove, onOpen }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);

  const drop = (status) => {
    if (dragId) {
      const t = tasks.find((x) => x.id === dragId);
      if (t && t.statut !== status) onMove(dragId, status);
    }
    setDragId(null); setOver(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {TASK_STATUSES.map((status) => {
        const col = tasks.filter((t) => t.statut === status);
        return (
          <div
            key={status}
            data-testid={`kanban-col-${status}`}
            onDragOver={(e) => { e.preventDefault(); setOver(status); }}
            onDragLeave={() => setOver(null)}
            onDrop={() => drop(status)}
            className={`rounded-xl border border-t-4 ${COL_ACCENT[status]} bg-slate-50/60 p-3 min-h-[200px] transition-colors ${over === status ? "bg-brand-50 ring-2 ring-brand-300" : ""}`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <h4 className="font-head font-semibold text-sm text-slate-700">{status}</h4>
              <span className="rounded-full bg-white border px-2 py-0.5 text-xs font-mono text-slate-500">{col.length}</span>
            </div>
            <div className="space-y-2">
              {col.map((t) => (
                <div
                  key={t.id}
                  draggable
                  onDragStart={() => setDragId(t.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => onOpen && onOpen(t)}
                  data-testid={`kanban-card-${t.id}`}
                  className={`rounded-lg border bg-white p-3 shadow-sm cursor-grab active:cursor-grabbing hover:border-brand-400 transition-all ${dragId === t.id ? "opacity-40 scale-95" : ""}`}
                >
                  <p className="text-sm font-semibold text-slate-800 leading-snug">{t.titre}</p>
                  {t.project_name && <p className="text-[11px] text-slate-400 mt-1 truncate">{t.project_name}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <StatusBadge value={t.priorite} />
                    {t.date_echeance && (
                      <span className="flex items-center gap-1 text-[10px] text-slate-400 font-mono"><CalendarClock className="h-3 w-3" />{fmtDate(t.date_echeance)}</span>
                    )}
                  </div>
                  {t.responsable_name && (
                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t">
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1C3144] text-white text-[9px] font-bold">{t.responsable_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
                      <span className="text-[11px] text-slate-500 truncate">{t.responsable_name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
